import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useBrandAnswer } from "@fox1ck-org/auth/saas/react";
import { Button, EmptyState, StatusDot } from "@fox1ck-org/ui";

import { formatDayRange } from "@/lib/format";
import { plural, useT, type TKey } from "@/lib/i18n";
import { useListAffiliateNetworks } from "@/lib/services/network-service";
import { useListLanders } from "@/lib/services/lander-service";
import { useListOffers } from "@/lib/services/offer-service";
import { useListTrackingDomains } from "@/lib/services/tracking-domain-service";
import { useListTrafficSources } from "@/lib/services/traffic-source-service";
import { useDataHealth } from "@/lib/services/data-health-service";
import { useReportTimezone } from "@/lib/services/workspace-service";

/**
 * The two empties of board CampEmpty, each the screen's own empty state
 * (EmptyState size="lg"). No campaign at all — the brand's edge (board TEdges):
 * its motif, «Кампаний пока нет», one action and what a campaign needs, each
 * line the catalog's own answer. The board's «или перенести из Keitaro» is
 * left out: the tracker has no Keitaro migration screen to send it to. Campaigns but no traffic in the period: say so, when
 * the last click came (GetDataHealth's last visit, the status line's own
 * fact — the browser counts nothing), and offer a longer period or the list
 * without numbers.
 */
export function FirstCampaign() {
  const t = useT();
  const { motif } = useBrandAnswer();
  const networks = useListAffiliateNetworks({ limit: 1 });
  const sources = useListTrafficSources({ limit: 1 });
  const offers = useListOffers({ limit: 1 });
  const landers = useListLanders({ limit: 1 });
  const domains = useListTrackingDomains({ limit: 1 });
  const line = (label: string, total: number | undefined, first: string | undefined) => {
    const done = (total ?? 0) > 0;
    return (
      <li key={label} className="flex items-center gap-2">
        <StatusDot tone={done ? "ok" : "neutral"} />
        <span className={done ? "text-foreground" : "text-muted-foreground"}>
          {done ? t("camps.empty.has", { what: label, name: first || String(total) }) : t("camps.empty.none", { what: label })}
        </span>
      </li>
    );
  };
  return (
    <section className="flex min-h-0 flex-1 flex-col items-center justify-center rounded-lg border border-border bg-card p-8">
      <EmptyState
        size="lg"
        motif={motif}
        title={t("camps.empty.first.title")}
        description={t("camps.empty.first.body")}
        action={
          <Button asChild>
            <Link to="/campaigns/new">
              <Plus className="size-4" strokeWidth={1.5} />
              {t("camps.empty.first.cta")}
            </Link>
          </Button>
        }
      />
      <ul className="flex w-85 flex-col gap-1.5 text-sm">
        {line(t("camps.empty.partner"), networks.data?.total, networks.data?.items[0]?.name)}
        {line(t("camps.empty.source"), sources.data?.total, sources.data?.items[0]?.name)}
        {line(t("camps.empty.offer"), offers.data?.total, offers.data?.items[0]?.name)}
        {line(t("camps.empty.lander"), landers.data?.total, landers.data?.items[0]?.name)}
        {line(t("camps.empty.domain"), domains.data?.total, domains.data?.items[0]?.fqdn)}
      </ul>
    </section>
  );
}

export function NoTrafficInPeriod({
  from,
  to,
  campaigns,
  onLonger,
  onShowList,
}: {
  from: string;
  to: string;
  campaigns: number;
  onLonger: () => void;
  onShowList: () => void;
}) {
  const t = useT();
  const health = useDataHealth();
  const tz = useReportTimezone();
  const last = lastClickText(t, health.data?.traffic?.lastVisitAt, Date.now(), tz);
  const body = t("camps.empty.quiet.body", { count: campaigns, campaigns: plural(t("word.campaigns"), campaigns) });
  return (
    <section className="flex min-h-0 flex-1 flex-col items-center justify-center rounded-lg border border-border bg-card p-8">
      <EmptyState
        size="lg"
        eyebrow={t("camps.empty.quiet.eyebrow")}
        title={t("camps.empty.quiet.title", { period: formatDayRange(from, to) })}
        description={last ? `${body} ${last}` : body}
        action={
          <span className="flex gap-2">
            <Button variant="secondary" onClick={onLonger}>
              {t("camps.empty.quiet.longer")}
            </Button>
            <Button variant="ghost" onClick={onShowList}>
              {t("camps.empty.quiet.list")}
            </Button>
          </span>
        }
      />
    </section>
  );
}

const UNITS: readonly { seconds: number; word: TKey }[] = [
  { seconds: 86_400, word: "word.days" },
  { seconds: 3_600, word: "word.hours" },
  { seconds: 60, word: "word.minutes" },
];

/**
 * «Последний клик был 6 часов назад — 20.10 в 09:41.» (доска CampEmpty) from
 * the last visit's instant, in the workspace's zone; nothing when the health
 * has none (no visit in the last day). Pure.
 */
export function lastClickText(t: (k: TKey, vars?: Record<string, string | number>) => string, at: string | undefined, now: number, tz: string): string | undefined {
  const ms = at ? Date.parse(at) : NaN;
  if (!Number.isFinite(ms)) return undefined;
  const seconds = Math.max(0, (now - ms) / 1000);
  const unit = UNITS.find((u) => seconds >= u.seconds);
  const n = unit ? Math.round(seconds / unit.seconds) : Math.max(1, Math.round(seconds));
  const age = `${n} ${plural(t(unit ? unit.word : "word.seconds"), n)}`;
  const parts = new Intl.DateTimeFormat("ru-RU", { timeZone: tz, day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(new Date(ms));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  return t("camps.empty.quiet.last", { age, day: `${part("day")}.${part("month")}`, time: `${part("hour")}:${part("minute")}` });
}
